import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  RefreshCw, 
  Search, 
  AlertCircle, 
  CheckCircle2, 
  FileText, 
  History, 
  Calendar, 
  Filter, 
  X, 
  Terminal,
  Download,
  Activity
} from "lucide-react";
import { 
  Button, 
  Card, 
  DataTable, 
  Modal, 
  StatusBadge 
} from "../components/ui";
import { SwalWithTheme } from "../utils/sweetalert";
import BuscaParceiroJoin from "../components/integracao/BuscaParceiroJoin";
import { 
  listarLogsJoin, 
  dispararVarreduraProducao,
  sincronizarJoinManual, 
  obterStatusJoin,
  listarLogsDaExecucaoJoin 
} from "../services/joinIntegrationService";

function origemMeta(origem) {
  if (origem === "API") return { label: "API", variant: "info" };
  if (origem === "EVOLUCAO") return { label: "Evolução", variant: "success" };
  if (origem === "EVOLUCAO_RAPIDA") return { label: "Evolução (rápida)", variant: "info" };
  return { label: "Webhook", variant: "default" };
}

export default function IntegracaoJoin() {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [origemFilter, setOrigemFilter] = useState("");
  const [isSyncActive, setIsSyncActive] = useState(false);
  
  // Execution detailed logs modal state
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logSearchTerm, setLogSearchTerm] = useState("");
  const [appliedLogSearchTerm, setAppliedLogSearchTerm] = useState("");
  const [logStatusFilter, setLogStatusFilter] = useState("");
  const [selectedErrorLog, setSelectedErrorLog] = useState(null);
  const [ocultarSemAlteracoes, setOcultarSemAlteracoes] = useState(false);
  
  // Executions Paginação
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalPages: 0,
    totalElements: 0
  });

  // Log Paginação
  const [logPagination, setLogPagination] = useState({
    page: 0,
    size: 20,
    totalPages: 0,
    totalElements: 0
  });

  // Só a resposta da requisição mais recente é aplicada. Com a tabela grande, clicar em páginas
  // seguidas fazia uma resposta antiga chegar depois e sobrescrever a página certa.
  const execucoesRequestRef = useRef(0);
  const logsRequestRef = useRef(0);

  // silent: recarga automática — sem carregando na tabela e sem alerta de erro (tenta de novo depois).
  const carregarExecucoes = useCallback(async (pageIndex = 0, size = 20, { silent = false } = {}) => {
    const requestId = ++execucoesRequestRef.current;
    if (!silent) setLoading(true);
    try {
      const params = {
        page: pageIndex,
        size: size,
        status: statusFilter || undefined,
        origem: origemFilter || undefined
      };

      const res = await listarLogsJoin(params);
      if (requestId !== execucoesRequestRef.current) return;
      setExecutions(res.content || []);
      // PageResponse do backend devolve `page` (não `number`, que é o nome do Page do Spring).
      setPagination({
        page: res.page ?? 0,
        size: res.size ?? 20,
        totalPages: res.totalPages ?? 0,
        totalElements: res.totalElements ?? 0
      });
    } catch (error) {
      if (requestId !== execucoesRequestRef.current) return;
      console.error(error);
      if (silent) return;
      const msg = error?.response?.data?.message || "Erro ao carregar o histórico de execuções.";
      SwalWithTheme.fire({
        icon: "error",
        title: "Erro",
        text: msg
      });
    } finally {
      if (requestId === execucoesRequestRef.current) setLoading(false);
    }
  }, [statusFilter, origemFilter]);

  const carregarLogsDaExecucao = useCallback(async (execucaoId, pageIndex = 0, size = 20) => {
    if (!execucaoId) return;
    const requestId = ++logsRequestRef.current;
    setLoadingLogs(true);
    try {
      const params = {
        page: pageIndex,
        size: size,
        status: logStatusFilter || undefined,
        excluirSemAlteracoes: ocultarSemAlteracoes,
        q: appliedLogSearchTerm || undefined
      };
      const res = await listarLogsDaExecucaoJoin(execucaoId, params);
      if (requestId !== logsRequestRef.current) return;
      setExecutionLogs(res.content || []);
      setLogPagination({
        page: res.page ?? 0,
        size: res.size ?? 20,
        totalPages: res.totalPages ?? 0,
        totalElements: res.totalElements ?? 0
      });
    } catch (error) {
      if (requestId !== logsRequestRef.current) return;
      console.error(error);
      SwalWithTheme.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao carregar logs da execução."
      });
    } finally {
      if (requestId === logsRequestRef.current) setLoadingLogs(false);
    }
  }, [appliedLogSearchTerm, logStatusFilter, ocultarSemAlteracoes]);

  const verificarStatusSync = useCallback(async () => {
    try {
      const statusRes = await obterStatusJoin();
      setIsSyncActive(statusRes.running);
      return statusRes.running;
    } catch (error) {
      console.error("Erro ao verificar status da sincronização:", error);
      return false;
    }
  }, []);

  useEffect(() => {
    carregarExecucoes(0, pagination.size);
    verificarStatusSync();
  }, [statusFilter, origemFilter, carregarExecucoes, verificarStatusSync]);

  // Carregar logs da execução ao selecionar ou mudar filtros internos
  useEffect(() => {
    if (selectedExecution) {
      carregarLogsDaExecucao(selectedExecution.id, 0, logPagination.size);
    }
  }, [selectedExecution?.id, logStatusFilter, ocultarSemAlteracoes, carregarLogsDaExecucao]);

  // Sincronizar o selectedExecution com a lista de execuções em tempo real
  useEffect(() => {
    if (selectedExecution) {
      const updated = executions.find(e => e.id === selectedExecution.id);
      if (updated) {
        setSelectedExecution(updated);
      }
    }
  }, [executions]);

  // Polling de logs individuais quando a execução selecionada está em andamento (RUNNING)
  useEffect(() => {
    let intervalId;
    if (selectedExecution && selectedExecution.status === "RUNNING") {
      intervalId = setInterval(() => {
        carregarLogsDaExecucao(selectedExecution.id, logPagination.page, logPagination.size);
      }, 5000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedExecution?.id, selectedExecution?.status, logPagination.page, logPagination.size, carregarLogsDaExecucao]);

  // Polling para atualizar execuções enquanto roda em segundo plano
  useEffect(() => {
    let intervalId;
    if (isSyncActive) {
      intervalId = setInterval(async () => {
        const stillRunning = await verificarStatusSync();
        carregarExecucoes(0, pagination.size);
        if (!stillRunning) {
          clearInterval(intervalId);
          SwalWithTheme.fire({
            icon: "success",
            title: "Sincronização Concluída",
            text: "A sincronização em segundo plano foi finalizada. O histórico de execuções foi atualizado.",
            timer: 4000,
            showConfirmButton: false
          });
        }
      }, 5000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isSyncActive, carregarExecucoes, pagination.size, verificarStatusSync]);

  // Execução em andamento (varredura de evolução, webhook): recarrega a lista a cada 10s para mostrar o
  // progresso, que o backend agora grava a cada chamada. A sincronização manual já tem o próprio ciclo.
  const temExecucaoRodando = executions.some((execucao) => execucao.status === "RUNNING");
  useEffect(() => {
    if (!temExecucaoRodando || isSyncActive) return undefined;
    const intervalId = setInterval(() => {
      if (document.hidden) return;
      carregarExecucoes(pagination.page, pagination.size, { silent: true });
    }, 10000);
    return () => clearInterval(intervalId);
  }, [temExecucaoRodando, isSyncActive, carregarExecucoes, pagination.page, pagination.size]);

  const handleClearFilters = () => {
    setStatusFilter("");
    setOrigemFilter("");
  };

  const handleLogSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedLogSearchTerm(logSearchTerm);
  };

  const handleExportCSV = async () => {
    if (!selectedExecution) return;
    try {
      SwalWithTheme.fire({
        title: "Exportando CSV...",
        text: "Por favor, aguarde enquanto preparamos os dados de exportação.",
        allowOutsideClick: false,
        didOpen: () => {
          SwalWithTheme.showLoading();
        }
      });

      const totalElements = logPagination.totalElements || 1000;
      const res = await listarLogsDaExecucaoJoin(selectedExecution.id, {
        page: 0,
        size: totalElements,
        status: logStatusFilter || undefined,
        excluirSemAlteracoes: ocultarSemAlteracoes,
        q: appliedLogSearchTerm || undefined
      });
      
      const logsToExport = res.content || [];
      if (logsToExport.length === 0) {
        SwalWithTheme.fire({
          icon: "info",
          title: "Sem registros",
          text: "Nenhum log localizado para os filtros informados."
        });
        return;
      }

      const headers = ["Horário", "Parceiro", "ID Join", "Documento", "Status", "Mensagem", "Erro Técnico"];
      const rows = logsToExport.map(log => [
        log.dataExecucao ? new Date(log.dataExecucao).toLocaleString("pt-BR") : "",
        log.nome || "",
        log.idJoin || "",
        log.documento || "",
        log.status || "",
        log.mensagem || "",
        log.erroMensagem || ""
      ]);

      const csvContent = [
        headers.join(";"),
        ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(";"))
      ].join("\n");

      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `logs_execucao_${selectedExecution.codigoExecucao}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      SwalWithTheme.fire({
        icon: "success",
        title: "Exportado!",
        text: "O arquivo CSV foi gerado e o download iniciado.",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      SwalWithTheme.fire({
        icon: "error",
        title: "Erro ao exportar",
        text: "Não foi possível gerar e baixar o arquivo CSV."
      });
    }
  };

  // Varredura do churn sob demanda: ate agora so existia no agendamento das 04:00 ou por teste na
  // linha de comando, entao quem precisava do dado na hora ficava sem saida.
  const handleVarreduraChurn = async (rapida) => {
    const confirm = await SwalWithTheme.fire({
      title: rapida ? "Buscar digitações recentes?" : "Rodar varredura completa do churn?",
      text: rapida
        ? "Traz os últimos dias de digitação. Leva poucos segundos."
        : "Busca 56 dias de digitação de todos os gerentes: 120 chamadas à Join, cerca de 5 minutos. Roda em segundo plano.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: rapida ? "Buscar" : "Rodar varredura",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    try {
      const resposta = await dispararVarreduraProducao({ rapida });
      SwalWithTheme.fire({
        title: resposta?.status === "ALREADY_RUNNING" ? "Já está rodando" : "Iniciada",
        text: resposta?.message || "Acompanhe pelo histórico de execuções abaixo.",
        icon: resposta?.status === "ALREADY_RUNNING" ? "info" : "success",
        timer: 2600,
        showConfirmButton: false,
      });
      carregarExecucoes(0, pagination.size);
    } catch (erro) {
      SwalWithTheme.error(
        "Não deu",
        erro?.response?.data?.message || "Não foi possível iniciar a varredura."
      );
    }
  };

  const handleSyncNow = async () => {
    const confirm = await SwalWithTheme.fire({
      title: "Sincronizar com a Join?",
      text: "Isso executará a importação de todos os parceiros em segundo plano. Você poderá continuar navegando pela plataforma normalmente.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, Sincronizar",
      cancelButtonText: "Cancelar"
    });

    if (!confirm.isConfirmed) return;

    setSyncing(true);
    try {
      await sincronizarJoinManual();
      setIsSyncActive(true);
      SwalWithTheme.fire({
        icon: "success",
        title: "Sincronização Iniciada",
        text: "O processamento foi iniciado em segundo plano. Acompanhe o progresso na tabela de execuções.",
        timer: 4000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error(error);
      const msg = error?.response?.data?.message || "Falha ao iniciar a sincronização.";
      SwalWithTheme.fire({
        icon: "error",
        title: "Falha ao Sincronizar",
        text: msg
      });
    } finally {
      setSyncing(false);
    }
  };

  const parseUtcDate = (val) => {
    if (!val) return null;
    let normalized = val;
    if (typeof val === "string" && val.includes("T") && !val.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(val)) {
      normalized = val + "Z";
    }
    return new Date(normalized);
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return "-";
    const data = parseUtcDate(dataStr);
    if (!data || Number.isNaN(data.getTime())) return "-";
    return data.toLocaleString("pt-BR");
  };

  // Estatísticas rápidas baseadas nas execuções carregadas
  const stats = React.useMemo(() => {
    const total = pagination.totalElements;
    const erros = executions.filter(e => e.status === "ERROR").length;
    return {
      total,
      erros
    };
  }, [executions, pagination.totalElements]);

  // Definição das colunas para a tabela de execuções
  const columns = [
    {
      key: "dataInicio",
      header: "Data/Hora Início",
      width: 170,
      render: (val) => (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Calendar className="h-3.5 w-3.5" />
          {formatarData(val)}
        </span>
      )
    },
    {
      key: "origem",
      header: "Origem",
      width: 110,
      render: (val) => {
        const meta = origemMeta(val);
        return <StatusBadge status={meta.label} variant={meta.variant} />;
      }
    },
    {
      key: "codigoExecucao",
      header: "Cód. Execução",
      width: 160,
      render: (val) => (
        <span className="font-mono text-xs text-muted-foreground shrink-0 block truncate max-w-[150px]" title={val}>
          {val}
        </span>
      )
    },
    {
      key: "totalProcessados",
      header: "Itens Processados",
      width: 250,
      render: (val, row) => {
        if (row.origem === "WEBHOOK") {
          return <span className="text-xs text-muted-foreground">Processamento unitário (Webhook)</span>;
        }
        return (
          <div className="space-y-1">
            <div className="flex justify-between text-xs font-medium">
              <span className="text-muted-foreground">Total: {val}</span>
              <div className="flex gap-2">
                <span className="text-success">Sucessos: {row.sucessos}</span>
                <span className="text-destructive">Erros: {row.erros}</span>
              </div>
            </div>
            {val > 0 && (
              <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden flex">
                <div 
                  className="bg-success h-full animate-pulse-slow" 
                  style={{ width: `${(row.sucessos / val) * 100}%` }}
                  title={`${row.sucessos} Sucessos`}
                />
                <div 
                  className="bg-destructive h-full" 
                  style={{ width: `${(row.erros / val) * 100}%` }}
                  title={`${row.erros} Erros`}
                />
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: "status",
      header: "Status Final",
      width: 120,
      render: (val) => (
        <StatusBadge 
          status={val === "SUCCESS" ? "Sucesso" : val === "WARNING" ? "Aviso" : val === "RUNNING" ? "Executando" : "Erro"} 
          variant={val === "SUCCESS" ? "success" : val === "WARNING" ? "warning" : val === "RUNNING" ? "info" : "danger"} 
        />
      )
    }
  ];

  // Ações da tabela principal
  const actions = [
    {
      icon: FileText,
      title: "Visualizar Logs do Processamento",
      onClick: (row) => setSelectedExecution(row)
    }
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <header className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <History className="h-6 w-6 text-primary" />
            Integração Join
          </h1>
          <p className="page-subtitle">Acompanhe as execuções da API, do Webhook e da varredura de evolução usada pelo churn.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => handleVarreduraChurn(false)}
            className="w-full md:w-auto"
          >
            <Activity className="h-4 w-4 mr-2" />
            Varredura do churn
          </Button>
          <Button 
            onClick={handleSyncNow} 
            disabled={syncing || isSyncActive}
            className="w-full md:w-auto shadow-md transition-all active:scale-[0.98]"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing || isSyncActive ? "animate-spin" : ""}`} />
            {/* Só a API de parceiros: a varredura de evolução roda no agendamento diário. */}
            {isSyncActive ? "Sincronizando em 2º plano..." : "Sincronizar parceiros (API)"}
          </Button>
        </div>
      </header>

      {/* Busca por parceiro em todos os eventos, com o payload recebido: a ferramenta para investigar
          cadastro que não atualiza (ex.: carteira herdada em massa via webhook). */}
      <BuscaParceiroJoin />

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-card to-muted/20 border-border/80">
          <div className="p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <History className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Histórico de Execuções</p>
              <h3 className="text-2xl font-bold text-foreground mt-0.5">{stats.total} total</h3>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-card to-muted/20 border-border/80">
          <div className="p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-2xl bg-success/10 text-success flex items-center justify-center shrink-0">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Sincronizações Ativas</p>
              <h3 className="text-2xl font-bold text-success mt-0.5">Automática Diária</h3>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-card to-muted/20 border-border/80">
          <div className="p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Erros Identificados</p>
              <h3 className={`text-2xl font-bold mt-0.5 ${stats.erros > 0 ? "text-destructive" : "text-foreground"}`}>
                {stats.erros} {stats.erros === 1 ? "execução falhou" : "execuções falharam"} nesta página
              </h3>
            </div>
          </div>
        </Card>
      </div>

      {/* Indicador de Processamento em Segundo Plano */}
      {isSyncActive && (
        <div className="flex items-center gap-3 p-4 rounded-2xl border border-primary/20 bg-primary/5 text-primary animate-pulse shadow-sm">
          <RefreshCw className="h-5 w-5 animate-spin text-primary shrink-0" />
          <div className="space-y-0.5">
            <h4 className="font-semibold text-sm">Sincronização com a API em andamento...</h4>
            <p className="text-xs text-muted-foreground">O sistema está importando os parceiros em segundo plano. As execuções abaixo serão atualizadas automaticamente a cada 5 segundos.</p>
          </div>
        </div>
      )}

      {/* Painel de Filtros */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="w-full md:w-48 space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <Filter className="h-3.5 w-3.5" />
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full h-10 px-3 rounded-xl border border-border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="">Todos</option>
              <option value="SUCCESS">Sucesso</option>
              <option value="WARNING">Aviso</option>
              <option value="RUNNING">Executando</option>
              <option value="ERROR">Erro</option>
            </select>
          </div>

          <div className="w-full md:w-48 space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <Filter className="h-3.5 w-3.5" />
              Origem
            </label>
            <select
              value={origemFilter}
              onChange={(e) => setOrigemFilter(e.target.value)}
              className="w-full h-10 px-3 rounded-xl border border-border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="">Todas</option>
              <option value="API">API (Agendado/Manual)</option>
              <option value="WEBHOOK">Webhook (Tempo Real)</option>
              <option value="EVOLUCAO">Evolução (Churn)</option>
              <option value="EVOLUCAO_RAPIDA">Evolução rápida (20 min)</option>
            </select>
          </div>

          <div className="flex gap-2">
            <Button onClick={() => carregarExecucoes(0, pagination.size)} className="h-10 px-5 shrink-0">
              Filtrar
            </Button>
            {(statusFilter || origemFilter) && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClearFilters}
                className="h-10 px-3 border-border hover:border-destructive/60 hover:text-destructive"
                title="Limpar Filtros"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Tabela de Execuções */}
      <Card title="Sessões de Execuções e Webhooks">
        <DataTable
          columns={columns}
          data={executions}
          actions={actions}
          loading={loading}
          pagination={pagination}
          onPageChange={(pageIndex) => carregarExecucoes(pageIndex, pagination.size)}
          onPageSizeChange={(pageSize) => carregarExecucoes(0, pageSize)}
          emptyState="Nenhuma sessão de execução localizada para os filtros informados."
        />
      </Card>

      {/* Modal de Detalhes da Execução (Logs Internos) */}
      {selectedExecution && (
        <Modal
          isOpen={true}
          onClose={() => {
            setSelectedExecution(null);
            setExecutionLogs([]);
            setLogSearchTerm("");
            setAppliedLogSearchTerm("");
            setLogStatusFilter("");
            setOcultarSemAlteracoes(false);
          }}
          title={`Histórico da Sessão - ${selectedExecution.codigoExecucao}`}
          maxWidth="max-w-6xl"
          showActions={false}
        >
          <div className="space-y-4">
            {/* Metadados da Sessão */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-2xl bg-muted/30 border border-border">
              <div>
                <span className="text-xs text-muted-foreground block font-medium">Origem</span>
                <span className="text-sm font-semibold block mt-0.5">
                  <StatusBadge
                    status={origemMeta(selectedExecution.origem).label}
                    variant={origemMeta(selectedExecution.origem).variant}
                  />
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block font-medium">Início</span>
                <span className="text-sm font-semibold block mt-0.5">{formatarData(selectedExecution.dataInicio)}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block font-medium">Término</span>
                <span className="text-sm font-semibold block mt-0.5">{formatarData(selectedExecution.dataFim)}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block font-medium">Resultado</span>
                <span className="text-sm font-semibold block mt-0.5">
                  <StatusBadge 
                    status={selectedExecution.status === "SUCCESS" ? "Sucesso" : selectedExecution.status === "WARNING" ? "Aviso" : selectedExecution.status === "RUNNING" ? "Executando" : "Erro"} 
                    variant={selectedExecution.status === "SUCCESS" ? "success" : selectedExecution.status === "WARNING" ? "warning" : selectedExecution.status === "RUNNING" ? "info" : "danger"} 
                  />
                </span>
              </div>
            </div>

            {selectedExecution.origem !== "WEBHOOK" && (
              <div className="space-y-3 p-4 rounded-2xl bg-card border border-border/80">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <span className="text-xs text-muted-foreground block font-medium">Total Processados</span>
                    <span className="text-lg font-bold text-foreground mt-0.5">{selectedExecution.totalProcessados}</span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block font-medium">Sucessos</span>
                    <span className="text-lg font-bold text-success mt-0.5">{selectedExecution.sucessos}</span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block font-medium">Erros</span>
                    <span className="text-lg font-bold text-destructive mt-0.5">{selectedExecution.erros}</span>
                  </div>
                </div>
                {selectedExecution.totalProcessados > 0 && (
                  <div className="space-y-1 pt-1.5 border-t border-border/40">
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden flex">
                      <div 
                        className="bg-success h-full animate-pulse-slow" 
                        style={{ width: `${(selectedExecution.sucessos / selectedExecution.totalProcessados) * 100}%` }}
                        title={`${selectedExecution.sucessos} Sucessos`}
                      />
                      <div 
                        className="bg-destructive h-full" 
                        style={{ width: `${(selectedExecution.erros / selectedExecution.totalProcessados) * 100}%` }}
                        title={`${selectedExecution.erros} Erros`}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>Progresso: {Math.round(((selectedExecution.sucessos + selectedExecution.erros) / selectedExecution.totalProcessados) * 100)}%</span>
                      <span>Restante: {selectedExecution.totalProcessados - (selectedExecution.sucessos + selectedExecution.erros)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedExecution.mensagem && (
              <div className="p-3 rounded-xl bg-muted/20 border border-border text-xs text-muted-foreground">
                <strong>Resumo da Sessão:</strong> {selectedExecution.mensagem}
              </div>
            )}

            {/* Filtros Internos do Log */}
            <form onSubmit={handleLogSearchSubmit} className="space-y-2 pt-2">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={logSearchTerm}
                    onChange={(e) => setLogSearchTerm(e.target.value)}
                    placeholder="Filtrar por parceiro, CPF, CNPJ ou ID..."
                    className="w-full h-10 pl-9 pr-4 rounded-xl border border-border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  />
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                </div>
                <div className="w-full md:w-44">
                  <select
                    value={logStatusFilter}
                    onChange={(e) => setLogStatusFilter(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <option value="">Status (Todos)</option>
                    <option value="SUCCESS">Sucesso</option>
                    <option value="WARNING">Aviso</option>
                    <option value="ERROR">Erro</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="h-10 px-4 shrink-0">
                    Buscar
                  </Button>
                  {(logSearchTerm || logStatusFilter || ocultarSemAlteracoes) && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setLogSearchTerm("");
                        setAppliedLogSearchTerm("");
                        setLogStatusFilter("");
                        setOcultarSemAlteracoes(false);
                      }}
                      className="h-10 px-3 border-border hover:border-destructive/60 hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleExportCSV}
                    className="h-10 px-4 shrink-0 border-success/20 hover:border-success/50 text-success hover:bg-success/5"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Exportar CSV
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2 px-1">
                <input
                  type="checkbox"
                  id="ocultarSemAlteracoes"
                  checked={ocultarSemAlteracoes}
                  onChange={(e) => setOcultarSemAlteracoes(e.target.checked)}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-primary bg-background"
                />
                <label htmlFor="ocultarSemAlteracoes" className="text-xs text-muted-foreground select-none cursor-pointer">
                  Ocultar parceiros sem alterações (exibir apenas novidades/erros)
                </label>
              </div>
            </form>

            {/* Listagem Individual de Logs da Execução */}
            <div className="border border-border/80 rounded-xl overflow-x-auto mt-2">
              <DataTable
                columns={[
                  {
                    key: "dataExecucao",
                    header: "Horário",
                    width: 120,
                    render: (val) => {
                      const date = parseUtcDate(val);
                      const timeStr = date && !Number.isNaN(date.getTime()) ? date.toLocaleTimeString("pt-BR") : "-";
                      return (
                        <span className="text-xs text-muted-foreground">{timeStr}</span>
                      );
                    }
                  },
                  {
                    key: "nome",
                    header: "Parceiro / Identificação",
                    width: 280,
                    render: (val, row) => (
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground text-xs truncate">{val || "Não identificado"}</p>
                        <div className="flex gap-2 text-[10px] text-muted-foreground mt-0.5">
                          {row.idJoin && <span>ID: <strong className="text-foreground/80">{row.idJoin}</strong></span>}
                          {row.documento && <span>Doc: <strong className="text-foreground/80">{row.documento}</strong></span>}
                        </div>
                      </div>
                    )
                  },
                  {
                    key: "mensagem",
                    header: "Log de Detalhes",
                    width: 320,
                    render: (val, row) => (
                      <p className={`text-xs ${row.status === 'ERROR' ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                        {val}
                      </p>
                    )
                  },
                  {
                    key: "status",
                    header: "Status",
                    width: 100,
                    render: (val) => (
                      <StatusBadge 
                        status={val === "SUCCESS" ? "Sucesso" : val === "WARNING" ? "Aviso" : "Erro"} 
                        variant={val === "SUCCESS" ? "success" : val === "WARNING" ? "warning" : "danger"} 
                      />
                    )
                  }
                ]}
                data={executionLogs}
                actions={[
                  {
                    icon: FileText,
                    title: "Ver Stack Trace do Erro",
                    hidden: (row) => row.status !== "ERROR",
                    onClick: (row) => setSelectedErrorLog(row)
                  }
                ]}
                loading={loadingLogs}
                pagination={logPagination}
                onPageChange={(pageIndex) => carregarLogsDaExecucao(selectedExecution.id, pageIndex, logPagination.size)}
                onPageSizeChange={(pageSize) => carregarLogsDaExecucao(selectedExecution.id, 0, pageSize)}
                emptyState="Nenhum log individual localizado nesta execução."
              />
            </div>

            <div className="flex justify-end border-t border-border pt-4">
              <Button 
                variant="outline"
                onClick={() => {
                  setSelectedExecution(null);
                  setExecutionLogs([]);
                  setLogSearchTerm("");
                  setLogStatusFilter("");
                  setOcultarSemAlteracoes(false);
                }}
              >
                Fechar
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de Stack Trace de Erro (Interno) */}
      {selectedErrorLog && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedErrorLog(null)}
          title="Erro de Importação - Detalhes Técnicos"
          maxWidth="max-w-2xl"
          showActions={false}
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-xl border border-destructive/20 bg-destructive/5 text-destructive">
              <AlertCircle className="h-5 w-5 shrink-0 mt-0.5 text-destructive" />
              <div className="space-y-1">
                <h4 className="font-semibold text-sm">Falha no Processamento do Parceiro</h4>
                <p className="text-xs">{selectedErrorLog.mensagem}</p>
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                <Terminal className="h-3.5 w-3.5" />
                Erro Retornado pelo Servidor
              </span>
              <div className="max-h-60 overflow-y-auto p-4 rounded-xl border border-border bg-muted/40 font-mono text-xs text-foreground whitespace-pre-wrap select-text leading-relaxed">
                {selectedErrorLog.erroMensagem || "Nenhuma mensagem técnica adicional foi retornada."}
              </div>
            </div>

            <div className="flex justify-end border-t border-border pt-4">
              <Button size="sm" variant="outline" onClick={() => setSelectedErrorLog(null)}>
                Voltar aos logs
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
